import React, { useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { X, Camera, Send, Bug } from 'lucide-react';
import { UserProfile } from '../types';

interface HelpReporterModalProps {
  onClose: () => void;
  currentUserProfile: UserProfile | null;
  appVersion: string;
}

export default function HelpReporterModal({ onClose, currentUserProfile, appVersion }: HelpReporterModalProps) {
  const [description, setDescription] = useState('');
  const [isCapturing, setIsCapturing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null);

  useEffect(() => {
    // Capture screenshot as soon as modal opens
    // We delay slightly to ensure the modal itself is not fully rendering or to let animations finish
    const captureScreen = async () => {
      try {
        // Hide the modal temporarily during capture to avoid infinite mirroring if possible
        // Actually, we WANT to capture the screen state before the modal fully covers it, 
        // but since we open the modal, we can just capture the body. It will include the modal backdrop.
        const canvas = await html2canvas(document.body, {
          scale: 0.8, // Scale down to save space
          logging: false,
          useCORS: true, // Attempt to load cross-origin images
        });
        
        // Compress heavily to stay under 1MB Firestore limit
        const base64 = canvas.toDataURL('image/jpeg', 0.4);
        setScreenshotBase64(base64);
      } catch (err) {
        console.error("Screenshot capture failed:", err);
        toast.error("Failed to capture visual scan. You can still submit a manual report.");
      } finally {
        setIsCapturing(false);
      }
    };

    captureScreen();
  }, []);

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Please provide a description of the anomaly.");
      return;
    }

    if (!currentUserProfile) {
      toast.error("Authentication error. Please log in.");
      return;
    }

    setIsSubmitting(true);
    try {
      const ticketNumber = `TIC-${Math.random().toString(36).substring(2, 6).toUpperCase()}-${Math.floor(Math.random() * 1000)}`;
      
      await addDoc(collection(db, 'helpTickets'), {
        ticketNumber,
        description,
        reportedBy: currentUserProfile.uid,
        reportedByName: `${currentUserProfile.firstName} ${currentUserProfile.lastName}`,
        screenshotBase64: screenshotBase64 || '',
        reportedAt: new Date().toISOString(),
        appVersion: appVersion,
        status: 'open'
      });

      toast.success(`Ticket ${ticketNumber} dispatched to Engineering.`);
      onClose();
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'helpTickets');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[var(--background)]/80 backdrop-blur-sm p-4">
      <div className="bg-[var(--background)] border-2 border-lcars-blue rounded-[40px] w-full max-w-2xl overflow-hidden flex flex-col shadow-2xl relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-lcars-blue hover:text-[var(--foreground)] transition-colors"
          aria-label="Close Engineering Support Modal"
        >
          <X className="w-8 h-8" />
        </button>

        <div className="flex items-center gap-4 border-b-4 border-lcars-blue/30 p-6 bg-lcars-blue/10">
          <Bug className="w-10 h-10 text-lcars-blue animate-pulse" />
          <div>
            <h2 className="text-2xl font-bold text-lcars-blue uppercase tracking-widest">Engineering Support</h2>
            <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold uppercase tracking-widest mt-1">
              Anomaly Reporting Interface
            </p>
          </div>
        </div>

        <div className="p-6 space-y-6 flex-1 overflow-y-auto">
          <div className="space-y-2">
            <div className="text-[0.65rem] text-lcars-blue font-bold uppercase tracking-widest block ml-2">
              Visual Scan (Automated Screenshot)
            </div>
            <div className="bg-[var(--background)]/50 border border-lcars-blue/30 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px] relative">
              {isCapturing ? (
                <div className="flex flex-col items-center text-lcars-blue gap-2 animate-pulse">
                  <Camera className="w-8 h-8" />
                  <span className="text-xs font-bold uppercase tracking-widest">Scanning Visual Interface...</span>
                </div>
              ) : screenshotBase64 ? (
                <img src={screenshotBase64} alt="Screen capture" className="w-full h-auto opacity-80" />
              ) : (
                <div className="text-[var(--foreground)]/40 text-xs font-bold uppercase tracking-widest">
                  Scan Failed - Manual Text Required
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="anomaly-description" className="text-[0.65rem] text-lcars-blue font-bold uppercase tracking-widest block ml-2">
              Anomaly Description
            </label>
            <textarea
              id="anomaly-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what went wrong, what you expected, or what should be fixed..."
              className="w-full min-h-[150px] bg-[var(--background)]/50 border border-lcars-blue text-[var(--foreground)] p-4 rounded-xl focus:ring-1 focus:ring-lcars-blue outline-none text-sm placeholder:text-[var(--foreground)]/20"
            />
          </div>
        </div>

        <div className="p-6 bg-white/5 border-t border-white/10 flex justify-between items-center">
           <div className="text-[0.6rem] text-[var(--foreground)]/40 uppercase font-bold max-w-[200px]">
             Submitting will attach diagnostic imagery to your report.
           </div>
           <button
             onClick={handleSubmit}
             disabled={isSubmitting || isCapturing}
             className="lcars-pill bg-lcars-blue text-black font-bold text-sm px-8 py-3 flex items-center gap-2 hover:bg-white transition-all disabled:opacity-50"
           >
             {isSubmitting ? (
               'TRANSMITTING...'
             ) : (
               <>
                 <Send className="w-4 h-4" /> DISPATCH REPORT
               </>
             )}
           </button>
        </div>
      </div>
    </div>
  );
}
